// Shared cross-component stores for endings + streak.
//
// Same useSyncExternalStore pattern as spark-store.js. Without this,
// each component that calls useGameState() gets ITS OWN useState copy,
// so when StoryPlayer adds an ending, Home / Stickers / Streaks don't
// see the update until they remount and re-read localStorage. The
// stale-UI bug surfaces as "I just earned a sticker but my Stickers
// page didn't refresh".
//
// game-state.js owns the localStorage shape; this file just wraps the
// in-memory value shared across React subscribers.

import { useSyncExternalStore } from 'react'

// ── Endings store ─────────────────────────────────────────────
let endings = []
const endingsListeners = new Set()

function notifyEndings() {
  for (const fn of endingsListeners) fn()
}

export function setEndingsGlobal(value) {
  const next = typeof value === 'function' ? value(endings) : value
  if (next === endings) return
  endings = Array.isArray(next) ? next : []
  notifyEndings()
}

export function getEndingsGlobal() {
  return endings
}

export function useEndingsFound() {
  return useSyncExternalStore(
    (cb) => {
      endingsListeners.add(cb)
      return () => endingsListeners.delete(cb)
    },
    () => endings,
  )
}

// ── Developer flag ────────────────────────────────────────────
// Two layers of state:
//
//   isDev          — server-side flag on the device row (admin toggles
//                    devices.is_dev via the admin panel; the client
//                    picks it up on register/ping responses).
//   devModeEnabled — client-side toggle (this device wants to SEE the
//                    dev features right now). Lets a flagged dev
//                    preview the experience as a regular user.
//
// useIsDev()      → just the device flag (used to gate the Settings
//                   toggle itself — non-devs never see the option).
// useDevMode()    → the EFFECTIVE state (device AND client).
//                   Components gate dev features on this.
let isDev = false
const isDevListeners = new Set()

const DEV_MODE_KEY = 'mp-dev-mode-disabled'
function readDevModeOverride() {
  try { return localStorage.getItem(DEV_MODE_KEY) === '1' } catch { return false }
}
let devModeOverrideOff = readDevModeOverride()
const devModeListeners = new Set()

function notifyIsDev() {
  for (const fn of isDevListeners) fn()
  // Server flag changes also re-derive the effective dev mode value.
  for (const fn of devModeListeners) fn()
}

function notifyDevMode() {
  for (const fn of devModeListeners) fn()
}

export function setIsDevGlobal(value) {
  const next = Boolean(value)
  if (next === isDev) return
  isDev = next
  notifyIsDev()
}

export function getIsDevGlobal() {
  return isDev
}

export function useIsDev() {
  return useSyncExternalStore(
    (cb) => {
      isDevListeners.add(cb)
      return () => isDevListeners.delete(cb)
    },
    () => isDev,
  )
}

// Effective dev mode — true only when both server flag is set AND
// the user hasn't toggled the client override off.
export function getDevModeGlobal() {
  return isDev && !devModeOverrideOff
}

export function setDevModeEnabled(enabled) {
  const newOverride = !enabled
  if (newOverride === devModeOverrideOff) return
  devModeOverrideOff = newOverride
  try {
    if (newOverride) localStorage.setItem(DEV_MODE_KEY, '1')
    else localStorage.removeItem(DEV_MODE_KEY)
  } catch {}
  notifyDevMode()
}

export function useDevMode() {
  return useSyncExternalStore(
    (cb) => {
      devModeListeners.add(cb)
      return () => devModeListeners.delete(cb)
    },
    () => isDev && !devModeOverrideOff,
  )
}

// ── Streak store ──────────────────────────────────────────────
const DEFAULT_STREAK = { current: 0, best: 0, lastPlayDate: null }
let streak = DEFAULT_STREAK
const streakListeners = new Set()

function notifyStreak() {
  for (const fn of streakListeners) fn()
}

export function setStreakGlobal(value) {
  const next = typeof value === 'function' ? value(streak) : value
  if (next === streak) return
  streak = next || DEFAULT_STREAK
  notifyStreak()
}

export function getStreakGlobal() {
  return streak
}

export function useStreak() {
  return useSyncExternalStore(
    (cb) => {
      streakListeners.add(cb)
      return () => streakListeners.delete(cb)
    },
    () => streak,
  )
}
